// styles.ts
import { styled } from '@mui/material/styles';
import { Box, Paper, Typography, Chip, Avatar, Badge } from '@mui/material';
import { PriorityType, StatusType } from '../../enums';

interface CardContainerProps {
  $statusColor?: string;
}

// A animação agora está definida no arquivo index.css

export const CardContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== '$statusColor',
})<CardContainerProps>(({ theme, $statusColor }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  backgroundColor: '#2a2a2a',
  color: theme.palette.text.primary,
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  cursor: 'pointer',
  transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out, border-left-color 0.3s ease',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '80px',
  width: '100%', // Garante que o card ocupe todo o espaço disponível na coluna
  borderLeft: $statusColor ? `5px solid ${$statusColor}` : 'none',
  paddingLeft: $statusColor ? theme.spacing(1) : theme.spacing(1.5),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
    borderLeftWidth: $statusColor ? '6px' : 'none',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  // A animação agora é controlada pelo CSS global no index.css
}));

export const ScrollableTextContainer = styled(Box)({
  maxWidth: 'calc(100% - 50px)',
  overflow: 'hidden',
  position: 'relative',
  flexGrow: 1,
  '&:hover .scrollable-title': {
    textOverflow: 'clip',
    overflow: 'visible',
  }
});

export const CardTitle = styled(Typography)({
  fontWeight: 500,
  lineHeight: 1.4,
  flexGrow: 1,
  marginRight: '8px',
  fontSize: '0.9rem',
  // Propriedades para truncar texto com elipses
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Comportamento para não quebrar palavras
  overflowWrap: 'normal',
  // Preparação para animação de hover
  transition: 'transform 0.3s ease-out',
  '&:hover': {
    transform: 'translateX(calc(-100% + 100%))',
    transition: 'transform 5s linear',
  }
});

export const Footer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  marginTop: 'auto',
  paddingTop: theme.spacing(1),
}));

export const MetadataContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  flexWrap: 'wrap',
  gap: '8px',
});

export const UserAttachmentsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const AttachmentsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

interface PriorityBadgeProps {
  priority?: PriorityType;
}

export const PriorityBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'priority',
})<PriorityBadgeProps>(({ theme, priority }) => {
  const getPriorityColor = () => {
    switch (priority) {
      case 'high':
        return '#f44336';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: getPriorityColor(),
    display: 'inline-block',
    marginLeft: '8px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  };
});

interface DeadlineContainerProps {
  $status?: 'normal' | 'warning' | 'expired';
}

export const DeadlineContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$status',
})<DeadlineContainerProps>(({ theme, $status = 'normal' }) => {
  // Configurações baseadas no status
  const getColorConfig = () => {
    switch ($status) {
      case 'expired':
        return {
          color: '#f44336', // Vermelho
          fontWeight: 'bold',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
          border: '1px solid rgba(244, 67, 54, 0.3)',
        };
      case 'warning':
        return {
          color: '#f39c12', // Amarelo alaranjado
          fontWeight: 'bold',
          backgroundColor: 'rgba(243, 156, 18, 0.1)',
          border: '1px solid rgba(243, 156, 18, 0.3)',
        };
      default: // normal
        return {
          color: theme.palette.text.secondary,
          fontWeight: 'normal',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: 'none',
        };
    }
  };

  const colorConfig = getColorConfig();

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.75rem',
    padding: '2px 6px',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.3s, color 0.3s, border 0.3s',
    ...colorConfig,
  };
});

export const MetadataItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

export const AssigneeAvatar = styled(Avatar)({
  width: 24,
  height: 24,
  fontSize: '0.75rem',
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  border: '2px solid #2a2a2a',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
});

export const CountBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    fontSize: '0.65rem',
    height: '16px',
    minWidth: '16px',
    padding: '0 4px',
    background: theme.palette.grey[600],
  },
}));

interface StatusBadgeProps {
  status?: StatusType;
}

export const StatusBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})<StatusBadgeProps>(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'Todo':
        return '#6c757d'; // Cinza
      case 'InProgress':
        return '#3498db'; // Azul
      case 'Review':
        return '#f39c12'; // Laranja
      case 'Done':
        return '#2ecc71'; // Verde
      case 'Blocked':
        return '#e74c3c'; // Vermelho
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    height: '22px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    backgroundColor: getStatusColor(),
    color: 'white',
    padding: '0 8px',
    marginRight: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    '& .MuiChip-label': {
      padding: '0 4px',
    },
  };
});